import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PlantBadge, Section, Heading, Spinner } from "../components/Bits";
import PageHero from "../components/PageHero";
import LoginGate from "../components/LoginGate";
import { Upload, Camera, AlertTriangle } from "lucide-react";

export default function Identify() {
  const { user } = useAuth() || {};
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) {
      toast.error("Bitte zuerst ein Foto auswählen");
      return;
    }
    setLoading(true);
    const fd = new FormData();
    fd.append("image", file);
    try {
      const r = await api.post("/identify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      toast.error("Erkennung fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section>
      <PageHero slug="identify" variant="soft"
        defaultOverline="KI-Erkennung"
        defaultTitle="Pflanze per Foto erkennen"
        defaultSubtitle="Wir nutzen Pl@ntNet und gleichen das Ergebnis mit unserer luxemburgischen Datenbank ab. HEIC-Fotos vom iPhone werden automatisch konvertiert."
      />

      {/* Safety + privacy disclaimer */}
      <div
        className="mb-6 rounded-2xl p-4 md:p-5 border-l-4 flex gap-3"
        style={{ borderColor: "#D4A017", backgroundColor: "#FFF8E1", color: "#5C4300" }}
        data-testid="identify-safety-notice"
      >
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed space-y-2">
          <div>
            <b>Keine 100 %-Garantie.</b> Die KI-Erkennung ist ein Hilfsmittel, <b>sie ersetzt keine sichere Bestimmung</b>.
            Esse im Zweifel <b>nichts</b> und zieh einen erfahrenen Menschen oder eine geführte Tour hinzu.
            Bei Verdacht auf Vergiftung: <b>Centre Antipoisons Luxembourg 8002&nbsp;5500</b> (24/7, kostenlos) · bei Bewusstlosigkeit/Atemnot <b>112</b>.
          </div>
          <div className="text-xs opacity-80">
            🔒 <b>Datenschutz:</b> Dein Foto wird nur zur Erkennung an{" "}
            <a href="https://plantnet.org/en/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Pl@ntNet</a>{" "}
            übermittelt (CIRAD, Frankreich). Wir speichern das Bild selbst nicht; es dient ausschließlich der Live-Analyse.
            Details siehe <Link to="/impressum" className="underline">Datenschutz</Link>.
          </div>
        </div>
      </div>

      {!user && (
        <div className="mb-6"><LoginGate icon={Camera} title="Pflanze erkennen" subtitle="Foto-Erkennung nutzt Pl@ntNet. Bitte kurz einloggen – so schützen wir die kostenlose API-Quota für alle." /></div>
      )}

      {user && (
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="plant-card p-8">
          <label className="block">
            <div className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer hover:border-[var(--wp-primary)] transition-colors" style={{ borderColor: "var(--wp-border)" }} data-testid="upload-zone">
              {preview ? (
                <img src={preview} alt="Vorschau" className="w-full h-72 object-cover rounded-xl" data-testid="upload-preview" />
              ) : (
                <div className="py-8">
                  <Upload className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <div className="font-medium">Foto auswählen oder hierher ziehen</div>
                  <div className="text-xs opacity-60 mt-1">JPG, PNG, HEIC – idealerweise Blatt im Fokus</div>
                </div>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="upload-input"
            />
          </label>
          <button onClick={submit} disabled={!file || loading} className="btn-primary w-full justify-center mt-6" data-testid="identify-submit">
            <Camera className="w-4 h-4" /> {loading ? "Analysiere…" : "Pflanze erkennen"}
          </button>
        </div>

        <div className="plant-card p-8">
          <h3 className="font-display text-2xl mb-4">Ergebnis</h3>
          {loading && <Spinner label="Pl@ntNet analysiert dein Foto…" />}
          {!loading && !result && (
            <div className="text-sm opacity-70">Noch kein Foto hochgeladen. Lade eines hoch und drücke "Pflanze erkennen".</div>
          )}
          {result?.results && (
            <div className="space-y-4">
              {result.results.length === 0 && (
                <div className="text-sm">Pl@ntNet konnte nichts identifizieren – versuche ein schärferes Foto eines einzelnen Blatts.</div>
              )}
              {result.results.map((s, i) => (
                <div key={i} className="rounded-xl p-4 border" style={{ borderColor: "var(--wp-border)" }} data-testid={`identify-result-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-lg italic">{s.scientific_name}</div>
                      <div className="text-xs opacity-70">{s.family} {s.common_names?.length > 0 && `· ${s.common_names.slice(0, 3).join(", ")}`}</div>
                    </div>
                    <div className="text-sm font-semibold" style={{ color: "var(--wp-primary)" }}>{Math.round((s.score || 0) * 100)}%</div>
                  </div>
                </div>
              ))}
              {result.matches && result.matches.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-3">Treffer in unserer DB</div>
                  <div className="space-y-3">
                    {result.matches.map((m, i) => (
                      <Link key={i} to={`/plants/${m.slug}`} className="flex items-center gap-4 p-3 plant-card hover:bg-[var(--wp-cream)]" data-testid={`db-match-${m.slug}`}>
                        <img src={m.image_url} alt={m.name_de} className="w-14 h-14 object-cover rounded-lg" />
                        <div className="flex-1">
                          <div className="font-medium">{m.name_de}</div>
                          <div className="text-xs italic opacity-70">{m.name_lat}</div>
                        </div>
                        <PlantBadge category={m.category} />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs flex items-start gap-2 mt-6 p-3 rounded-lg" style={{ backgroundColor: "rgba(167,58,58,0.06)" }}>
                <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "var(--wp-toxic)" }} />
                <div>KI-Erkennungen sind nur ein Hinweis. Vor dem Essen immer manuell bestimmen lassen!</div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </Section>
  );
}
